/**
 * <facet-filters>: SSR-first filtering via the Section Rendering API.
 * Any change serializes the form, fetches the re-rendered section, and
 * swaps the grid — preserving focus, open panels, and history. Requests
 * abort superseded requests; the count live region survives the swap.
 * Below 750px the whole filter row becomes a focus-trapped drawer.
 * No JS = plain GET form.
 */
import { trapFocus, removeTrapFocus } from './global.js';
const MOBILE = '(max-width: 749px)';

class FacetFilters extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.sectionId = this.dataset.section;
    this.debounceTimer = null;
    this.abortController = null;

    this.form.addEventListener('change', () => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.applyFilters(), 250);
    });
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.applyFilters();
    });
    this.onPopstate = () => this.renderFromUrl(window.location.href, false);
    window.addEventListener('popstate', this.onPopstate);

    this.initDrawer();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopstate);
    document.removeEventListener('keyup', this.onKeyup);
    this.mediaQuery?.removeEventListener('change', this.onBreakpoint);
    this.abortController?.abort();
    this.closeDrawer({ restoreFocus: false });
  }

  /* ---------- Mobile drawer ----------
   * The panel is `display: contents` above 750px, so the same markup is a
   * plain filter row on desktop and a modal sheet on phones. Dialog
   * semantics are applied on open and removed on close, never left on the
   * desktop layout where they would be a lie.
   */
  initDrawer() {
    this.panel = this.querySelector('[data-facets-panel]');
    this.openButton = this.querySelector('[data-facets-open]');
    if (!this.panel || !this.openButton) return;

    this.openButton.addEventListener('click', () => this.openDrawer());
    this.querySelectorAll('[data-facets-close]').forEach((el) =>
      el.addEventListener('click', () => this.closeDrawer())
    );

    this.onKeyup = (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-drawer-open')) this.closeDrawer();
    };
    document.addEventListener('keyup', this.onKeyup);

    // Resizing past the breakpoint while open would strand the trap.
    this.mediaQuery = window.matchMedia(MOBILE);
    this.onBreakpoint = (e) => {
      if (!e.matches) this.closeDrawer({ restoreFocus: false });
    };
    this.mediaQuery.addEventListener('change', this.onBreakpoint);
  }

  setPageInert(inert) {
    ['.shopify-section-group-header-group', '.shopify-section-group-footer-group'].forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => el.toggleAttribute('inert', inert));
    });
    document.querySelectorAll('main > *').forEach((el) => {
      if (el.contains(this)) return;
      el.toggleAttribute('inert', inert);
    });
  }

  openDrawer() {
    if (!this.mediaQuery?.matches) return;
    this.classList.add('is-drawer-open');
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-modal', 'true');
    this.panel.setAttribute('aria-label', this.openButton.textContent.trim());
    this.openButton.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    this.setPageInert(true);
    trapFocus(this.panel, this.panel);
  }

  closeDrawer({ restoreFocus = true } = {}) {
    if (!this.classList.contains('is-drawer-open')) return;
    this.classList.remove('is-drawer-open');
    this.panel.removeAttribute('role');
    this.panel.removeAttribute('aria-modal');
    this.panel.removeAttribute('aria-label');
    this.openButton.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    this.setPageInert(false);
    removeTrapFocus(restoreFocus ? this.openButton : null);
  }

  serialize() {
    const formData = new FormData(this.form);
    const params = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      if (value !== '') params.append(key, value);
    }
    return params.toString();
  }

  applyFilters() {
    const url = `${window.location.pathname}?${this.serialize()}`;
    this.renderFromUrl(url, true);
  }

  snapshotFocus() {
    const active = document.activeElement;
    if (!active || !this.form.contains(active)) return null;
    return { name: active.name, value: active.value, tag: active.tagName };
  }

  restoreFocus(snapshot) {
    if (!snapshot) return;
    const candidates = [...this.form.querySelectorAll(`[name="${CSS.escape(snapshot.name)}"]`)];
    const match =
      candidates.find((el) => el.value === snapshot.value) || candidates[0];
    match?.focus();
  }

  async renderFromUrl(url, pushState) {
    const grid = document.querySelector('[data-collection-grid]');
    if (!grid) return;

    this.abortController?.abort();
    this.abortController = new AbortController();

    grid.setAttribute('aria-busy', 'true');
    grid.style.opacity = '0.4';
    const focusSnapshot = this.snapshotFocus();

    try {
      const response = await fetch(
        `${url}${url.includes('?') ? '&' : '?'}section_id=${this.sectionId}`,
        { signal: this.abortController.signal }
      );
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      const newGrid = html.querySelector('[data-collection-grid]');
      if (newGrid) grid.innerHTML = newGrid.innerHTML;

      const newCount = html.querySelector('[data-results-count]');
      const count = document.querySelector('[data-results-count]');
      if (newCount && count) count.textContent = newCount.textContent;

      const newFacets = html.querySelector('facet-filters form');
      if (newFacets) {
        const openPanels = [...this.form.querySelectorAll('details[open][data-filter]')].map(
          (d) => d.dataset.filter
        );
        this.form.innerHTML = newFacets.innerHTML;
        this.form.querySelectorAll('details[data-filter]').forEach((d) => {
          if (openPanels.includes(d.dataset.filter)) d.setAttribute('open', '');
        });
        this.restoreFocus(focusSnapshot);
      }

      if (pushState) window.history.pushState({}, '', url);
    } catch (e) {
      if (e.name === 'AbortError') return;
      window.location.href = url;
    } finally {
      grid.style.opacity = '';
      grid.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('facet-filters')) {
  customElements.define('facet-filters', FacetFilters);
}
